import { useEffect, useMemo, useRef, useState } from 'react';

import { cn } from '~/utils/cn';

import { Text } from '../../atoms/Text/view';
import type { PropsLineChart } from './interfaces';

const GRID_LINES = 3;
const AXIS_W = 56;
const PAD = 10;
const LABEL_H = 20;
const STROKE_W = 2.5;
const DOT_R = 4.5;

export default function LineChart({
  points,
  height = 160,
  strokeColor = '#1D4733',
  fillColor,
  showLabels = false,
  showYAxis = false,
  formatValue,
  className,
}: PropsLineChart) {
  const containerRef = useRef<HTMLDivElement>(null);
  const gradId = useRef(`lc${Math.random().toString(36).slice(2, 7)}`).current;
  const [width, setWidth] = useState(0);
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    setWidth(el.clientWidth);
    const observer = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width ?? el.clientWidth;
      if (w > 0) setWidth(Math.floor(w));
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const hasAxis = showYAxis && !!formatValue;
  const axisW = hasAxis ? AXIS_W : 0;
  const labelH = showLabels ? LABEL_H : 0;
  const svgW = width;
  const svgH = height;

  const cx0 = axisW + PAD;
  const cx1 = svgW - PAD;
  const cy0 = PAD;
  const cy1 = svgH - PAD - labelH;
  const cW = Math.max(1, cx1 - cx0);
  const cH = Math.max(1, cy1 - cy0);

  const computed = useMemo(() => {
    if (!points.length || width === 0) return null;

    const values = points.map((p) => p.value);
    const maxV = Math.max(...values);
    const minV = Math.min(...values);
    const range = maxV - minV || 1;

    const toX = (i: number) =>
      points.length === 1 ? cx0 + cW / 2 : cx0 + (i / (points.length - 1)) * cW;
    const toY = (v: number) => cy0 + cH - ((v - minV) / range) * cH;

    const coords = points.map((p, i) => ({ x: toX(i), y: toY(p.value) }));

    const d = coords
      .map((c, i) => `${i === 0 ? 'M' : 'L'} ${c.x.toFixed(1)} ${c.y.toFixed(1)}`)
      .join(' ');
    const f = `${d} L ${coords[coords.length - 1].x.toFixed(1)} ${cy1} L ${coords[0].x.toFixed(1)} ${cy1} Z`;

    const gridLevels = Array.from({ length: GRID_LINES }, (_, i) => {
      const v = minV + (range * i) / (GRID_LINES - 1);
      const y = toY(v);
      const clampedY = Math.max(cy0 + 6, Math.min(y, cy1 - 6));
      return { y, labelY: clampedY, v };
    });

    const stepX = points.length > 1 ? cW / (points.length - 1) : cW;
    const labelStep = Math.max(1, Math.ceil(points.length / 5));

    return { coords, d, f, gridLevels, minV, maxV, stepX, labelStep };
  }, [points, width, cx0, cW, cy0, cH, cy1]);

  const handleMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
    if (!computed || !points.length) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const mouseX = e.clientX - rect.left - cx0;
    const idx = Math.max(0, Math.min(Math.round(mouseX / computed.stepX), points.length - 1));
    setHoveredIdx(idx);
  };

  const hoveredCoord = hoveredIdx !== null ? computed?.coords[hoveredIdx] : null;
  const hoveredPoint = hoveredIdx !== null ? points[hoveredIdx] : null;

  const TOOLTIP_W = 148;
  const tooltipLeft = hoveredCoord
    ? Math.min(Math.max(hoveredCoord.x, TOOLTIP_W / 2 + 6), width - TOOLTIP_W / 2 - 6)
    : 0;
  const caretShift = hoveredCoord ? hoveredCoord.x - tooltipLeft : 0;
  const caretShiftSafe = Math.max(
    Math.min(caretShift, TOOLTIP_W / 2 - 16),
    -(TOOLTIP_W / 2 - 16),
  );

  if (!points.length) {
    return (
      <div
        ref={containerRef}
        className={cn('flex items-center justify-center w-full', className)}
        style={{ height }}
      >
        <Text className="text-bodyMD color-intense-06">Sem dados</Text>
      </div>
    );
  }

  const color = fillColor ?? strokeColor;

  return (
    <div ref={containerRef} className={cn('w-full relative', className)}>
      {hoveredCoord && hoveredPoint && (
        <div
          style={{
            position: 'absolute',
            left: tooltipLeft,
            top: Math.max(4, hoveredCoord.y - 10),
            transform: 'translateX(-50%) translateY(-100%)',
            zIndex: 20,
            pointerEvents: 'none',
            filter: 'drop-shadow(0 3px 10px rgba(0,0,0,0.12))',
          }}
        >
          <div
            style={{
              background: 'white',
              border: '1px solid #e2e8f0',
              borderRadius: 8,
              padding: '6px 12px',
              whiteSpace: 'nowrap',
            }}
          >
            <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 3 }}>
              {hoveredPoint.label}
            </div>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>
              {formatValue ? formatValue(hoveredPoint.value) : String(hoveredPoint.value)}
            </div>
          </div>
          <div
            style={{
              position: 'absolute',
              bottom: -7,
              left: `calc(50% + ${caretShiftSafe}px)`,
              transform: 'translateX(-50%) rotate(45deg)',
              width: 12,
              height: 12,
              background: 'white',
              borderRight: '1px solid #e2e8f0',
              borderBottom: '1px solid #e2e8f0',
            }}
          />
        </div>
      )}

      {width > 0 && computed ? (
        <svg
          width={svgW}
          height={svgH}
          viewBox={`0 0 ${svgW} ${svgH}`}
          className="block max-w-full"
          onMouseMove={handleMouseMove}
          onMouseLeave={() => setHoveredIdx(null)}
        >
          <defs>
            <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity="0.30" />
              <stop offset="75%" stopColor={color} stopOpacity="0.06" />
              <stop offset="100%" stopColor={color} stopOpacity="0" />
            </linearGradient>
            <clipPath id={`clip-${gradId}`}>
              <rect x={cx0} y={cy0} width={cW} height={cH} rx={10} ry={10} />
            </clipPath>
          </defs>

          {hasAxis &&
            computed.gridLevels.map((g, i) => (
              <text
                key={i}
                x={axisW - 6}
                y={g.labelY}
                textAnchor="end"
                dominantBaseline="middle"
                fontSize="10"
                fill="#b0b8c4"
                fontFamily="system-ui, -apple-system, sans-serif"
              >
                {formatValue!(g.v)}
              </text>
            ))}

          <g clipPath={`url(#clip-${gradId})`}>
            {computed.gridLevels.map((g, i) => (
              <line
                key={i}
                x1={cx0}
                y1={g.y}
                x2={cx1}
                y2={g.y}
                stroke="#e5e7eb"
                strokeWidth={1}
              />
            ))}

            <path d={computed.f} fill={`url(#${gradId})`} />

            <path
              d={computed.d}
              fill="none"
              stroke={strokeColor}
              strokeWidth={STROKE_W}
              strokeLinejoin="round"
              strokeLinecap="round"
            />

            {hoveredCoord && (
              <line
                x1={hoveredCoord.x}
                y1={cy0}
                x2={hoveredCoord.x}
                y2={cy1}
                stroke={strokeColor}
                strokeWidth={1}
                strokeDasharray="4,3"
                opacity={0.35}
              />
            )}

            {hoveredIdx === null && computed.coords.length > 0 && (() => {
              const last = computed.coords[computed.coords.length - 1];
              return (
                <>
                  <circle cx={last.x} cy={last.y} r={DOT_R + 2.5} fill="white" />
                  <circle cx={last.x} cy={last.y} r={DOT_R} fill={strokeColor} />
                </>
              );
            })()}

            {hoveredCoord && (
              <>
                <circle cx={hoveredCoord.x} cy={hoveredCoord.y} r={DOT_R + 2.5} fill="white" />
                <circle cx={hoveredCoord.x} cy={hoveredCoord.y} r={DOT_R} fill={strokeColor} />
              </>
            )}
          </g>

          {showLabels &&
            points.map((p, i) => {
              if (
                i !== 0 &&
                i !== points.length - 1 &&
                i % computed.labelStep !== 0
              )
                return null;
              return (
                <text
                  key={i}
                  x={computed.coords[i].x}
                  y={svgH - 4}
                  textAnchor={
                    i === 0 ? 'start' : i === points.length - 1 ? 'end' : 'middle'
                  }
                  fontSize="10"
                  fill="#b0b8c4"
                  fontFamily="system-ui, -apple-system, sans-serif"
                >
                  {p.label}
                </text>
              );
            })}
        </svg>
      ) : (
        <div style={{ height: svgH }} />
      )}
    </div>
  );
}
